// src/pages/DynamicDemo.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaCheckCircle, FaMagic, FaArrowRight, FaHotel, FaLock, 
  FaUsers, FaEye, FaCoins, FaShoppingBag, FaBolt, FaTicketAlt, 
  FaMapMarkerAlt, FaRobot, FaChartBar, FaUserEdit, FaListAlt, 
  FaHome, FaFire, FaTrash, FaArchive, FaSave, FaTimes
} from 'react-icons/fa';
import { supabase } from '../services/supabaseClient';

// --- COMPOSANTS AUXILIAIRES ---

const AnimatedCounter = ({ targetValue, duration = 2000, prefix = "", suffix = "" }: { targetValue: number, duration?: number, prefix?: string, suffix?: string }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const currentCount = Math.min(Math.floor((progress / duration) * targetValue), targetValue);
      setCount(currentCount);
      if (progress < duration) animationFrame = requestAnimationFrame(animate);
      else setCount(targetValue);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [targetValue, duration]);
  return <span>{prefix}{count.toLocaleString('fr-CA')}{suffix}</span>;
};

// --- COMPOSANT PRINCIPAL ---

export default function DynamicDemo() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [realCompanyName, setRealCompanyName] = useState<string>("Votre Entreprise");
  const [leadEmail, setLeadEmail] = useState<string>("");
  const [isDemoReady, setIsDemoReady] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Onglets du dashboard
  const [demoTab, setDemoTab] = useState<'overview' | 'stats'>('overview');

  // Données d'analyse déterministes (semblent réelles et restent stables pour chaque entreprise)
  const [simData, setSimData] = useState({
    views: 0,
    revenue: 0,
    maxAxis: 1200,
    stats: [] as {day: string, value: number}[]
  });

  useEffect(() => {
    // Générateur de données basé sur le nom de l'entreprise (Algorithme déterministe)
    const generateRealisticData = (slug: string) => {
      const seed = slug.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      
      const calculatedViews = 1500 + (seed * 13 % 3500);
      const calculatedRevenue = 800 + (seed * 7 % 2000);
      
      const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
      const stats = days.map((day, i) => {
        // Crée une courbe réaliste
        const baseVal = (calculatedRevenue / 7) * 0.5;
        const variance = (seed * (i + 3)) % (calculatedRevenue / 4);
        return { day, value: Math.floor(baseVal + variance) };
      });

      const maxValue = Math.max(...stats.map(s => s.value));
      const maxAxis = Math.ceil(maxValue / 400) * 400; // Arrondi à la centaine supérieure

      setSimData({
        views: calculatedViews,
        revenue: calculatedRevenue,
        maxAxis: maxAxis < 400 ? 400 : maxAxis, // Minimum 400 pour un beau graphique
        stats
      });
    };

    const fetchLeadData = async () => {
      if (companySlug) {
        generateRealisticData(companySlug); // On génère les données immédiatement

        const { data } = await supabase
          .from('sdr_leads')
          .select('*')
          .eq('demo_slug', companySlug)
          .maybeSingle();

        if (data) {
          setLeadId(data.id);
          setRealCompanyName(data.company_name);
          setLeadEmail(data.email || "");

          // 🔥 Tracking d'ouverture sécurisé
          // Le catch empêche l'erreur 400 de s'afficher dans la console si le visiteur n'a pas les droits RLS
          if (data.status === 'new' || data.status === 'sent' || data.status === 'drafted') {
            try {
              const { error } = await supabase
                .from('sdr_leads')
                .update({ status: 'viewed' })
                .eq('id', data.id);
              if (error) console.debug("Suivi d'ouverture bloqué par sécurité locale.");
            } catch (err) {
              // On ignore silencieusement pour ne pas perturber l'expérience client
            }
          }
        } else {
          const fallbackName = companySlug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          setRealCompanyName(fallbackName);
        }
      }
      setTimeout(() => {
        setLoading(false);
        setTimeout(() => setIsDemoReady(true), 100);
      }, 2000);
    };
    
    fetchLeadData();
  }, [companySlug]);

  // --- ACTIONS DE GESTION ---

  const handleUpdateLead = async () => {
    if (!leadId) return;
    const { error } = await supabase
      .from('sdr_leads')
      .update({ company_name: realCompanyName, email: leadEmail })
      .eq('id', leadId);
    
    if (!error) setIsEditing(false);
    else alert("Erreur lors de la mise à jour");
  };

  const handleArchiveLead = async () => {
    if (!leadId || !window.confirm("Archiver ce lead ?")) return;
    await supabase.from('sdr_leads').update({ status: 'archived' }).eq('id', leadId);
    navigate('/');
  };

  const handleDeleteLead = async () => {
    if (!leadId || !window.confirm("Supprimer définitivement ce lead ?")) return;
    await supabase.from('sdr_leads').delete().eq('id', leadId);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] animate-pulse"></div>
        <div className="relative z-10 flex flex-col items-center">
          <div className="w-20 h-20 relative mb-8">
            <div className="absolute inset-0 border-4 border-slate-700 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <FaMagic className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-400 text-2xl animate-pulse" />
          </div>
          <h2 className="text-2xl font-black mb-2">Analyse de marché en cours</h2>
          <p className="text-blue-400 font-bold uppercase tracking-widest text-xs">Extraction des données locales</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden pb-20">
      
      {/* --- BARRE DE CONTRÔLE ADMIN (Visible seulement pour toi/debug) --- */}
      <div className="bg-amber-50 border-b border-amber-200 p-2 flex justify-center gap-4 text-[10px] font-bold uppercase tracking-tighter">
        <button onClick={() => setIsEditing(true)} className="flex items-center gap-1 text-amber-700 hover:text-amber-900"><FaUserEdit/> Modifier</button>
        <button onClick={handleArchiveLead} className="flex items-center gap-1 text-slate-500 hover:text-slate-700"><FaArchive/> Archiver</button>
        <button onClick={handleDeleteLead} className="flex items-center gap-1 text-red-500 hover:text-red-700"><FaTrash/> Supprimer</button>
      </div>

      {/* HEADER */}
      <header className="bg-slate-900 text-white p-4 flex justify-between items-center shadow-lg sticky top-0 z-50 backdrop-blur-md bg-slate-900/90 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center font-black">F</div>
          <span className="font-bold tracking-widest text-xs uppercase">Forfeo Explore <span className="text-blue-400">x</span> {realCompanyName}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-black bg-slate-800 px-4 py-2 rounded-full border border-slate-700 text-green-400 uppercase">
          <FaLock /> Accès Privé
        </div>
      </header>

      {/* MODALE D'ÉDITION */}
      {isEditing && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-black mb-6">Modifier le Lead</h3>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400">Nom de l'entreprise</label>
                <input type="text" value={realCompanyName} onChange={(e) => setRealCompanyName(e.target.value)} className="w-full p-3 bg-slate-100 rounded-xl border-none focus:ring-2 focus:ring-blue-500 font-bold" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-slate-400">Email de contact</label>
                <input type="email" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} className="w-full p-3 bg-slate-100 rounded-xl border-none focus:ring-2 focus:ring-blue-500 font-bold" />
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={handleUpdateLead} className="flex-1 bg-slate-900 text-white py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2"><FaSave/> Sauvegarder</button>
              <button onClick={() => setIsEditing(false)} className="px-6 py-3 bg-slate-100 rounded-xl font-black text-sm"><FaTimes/></button>
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-5xl mx-auto p-4 md:p-12 transition-all duration-1000 transform ${isDemoReady ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        
        {/* HERO */}
        <div className="text-center mb-16 mt-10">
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
            Propulsez <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">{realCompanyName}</span>.
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto font-medium">
            Notre algorithme a analysé le potentiel de votre établissement. Voici comment Forfeo va générer de la croissance immédiate pour vous.
          </p>
        </div>

        {/* CARTES DE DÉMO (Chiffres Réalistes et Stables) */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-xl relative overflow-hidden group">
            <div className="relative z-10">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 text-white rounded-2xl flex items-center justify-center text-2xl mb-6 shadow-lg shadow-blue-500/30"><FaHotel /></div>
              <h3 className="text-2xl font-black mb-2">Liquidation "Last Call"</h3>
              <p className="text-slate-500 mb-8 text-sm italic">Analyse prédictive mensuelle pour vos invendus potentiels :</p>
              <div className="space-y-3">
                <div className="flex justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <span className="font-bold text-xs flex items-center gap-2"><FaEye className="text-blue-400"/> Vues locales estimées</span>
                  <span className="text-blue-600 font-black"><AnimatedCounter targetValue={simData.views} prefix="+" /></span>
                </div>
                <div className="flex justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <span className="font-bold text-xs flex items-center gap-2"><FaCoins className="text-green-400"/> Revenu récupérable</span>
                  <span className="text-green-600 font-black"><AnimatedCounter targetValue={simData.revenue} suffix=" $" /></span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-xl relative overflow-hidden group">
            <div className="relative z-10">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-2xl flex items-center justify-center text-2xl mb-6 shadow-lg shadow-purple-500/30"><FaUsers /></div>
              <h3 className="text-2xl font-black mb-2">Réseau d'Affaires B2B</h3>
              <p className="text-slate-500 mb-8 text-sm italic">Impact direct sur votre réseau de partenariats :</p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-purple-500 mt-1 shrink-0" />
                  <p className="text-xs font-bold text-slate-700">Mise en relation prioritaire sans intermédiaire corporatif.</p>
                </div>
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="text-purple-500 mt-1 shrink-0" />
                  <p className="text-xs font-bold text-slate-700">Zéro commission facturée sur la signature de vos contrats locaux.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* DASHBOARD SIMULATION */}
        <div className="mb-24">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black">Votre centre de pilotage</h2>
            <p className="text-slate-500 text-sm mt-2">Interface interactive (Aperçu de vos données futures)</p>
          </div>

          <div className="bg-slate-200/50 p-4 md:p-8 rounded-[3rem] border border-slate-200 shadow-inner">
            <div className="flex gap-2 mb-6">
              <button onClick={() => setDemoTab('overview')} className={`px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition ${demoTab === 'overview' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-400'}`}><FaHome className="inline mr-2"/> Vue d'ensemble</button>
              <button onClick={() => setDemoTab('stats')} className={`px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition ${demoTab === 'stats' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-400'}`}><FaChartBar className="inline mr-2"/> Statistiques</button>
            </div>

            {demoTab === 'overview' ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-in fade-in duration-500">
                {[
                  { icon: <FaListAlt/>, label: "Réservations", color: "text-blue-500", badge: "3" },
                  { icon: <FaFire/>, label: "Offres Flash", color: "text-rose-500" },
                  { icon: <FaRobot/>, label: "Agent IA", color: "text-emerald-500" },
                  { icon: <FaCoins/>, label: "Finances", color: "text-amber-500" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-[2rem] shadow-sm flex flex-col items-center text-center">
                    <div className={`text-3xl ${item.color} mb-3`}>{item.icon}</div>
                    <span className="font-extrabold text-[10px] uppercase text-slate-800">{item.label}</span>
                    {item.badge && <span className="mt-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-black">{item.badge}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden animate-in slide-in-from-bottom-4 duration-500">
                <div className="flex justify-between items-center mb-8">
                  <h3 className="text-lg font-black flex items-center gap-2"><FaChartBar className="text-cyan-400" /> Évolution des ventes (Prévision 7j)</h3>
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-white/5 px-3 py-1 rounded-lg">Impact : +24%</div>
                </div>

                {/* ✅ LE GRAPHIQUE CORRIGÉ : h-48 + flex-col avec justify-end garanti l'affichage des barres */}
                <div className="relative h-48 flex items-end justify-between gap-2 px-2">
                  
                  {/* Axes et Lignes de fond (dynamiques basés sur maxAxis) */}
                  <div className="absolute inset-0 flex flex-col justify-between opacity-10 text-[11px] font-black pointer-events-none">
                    <div className="border-b border-white w-full flex justify-end pb-1">{simData.maxAxis}$</div>
                    <div className="border-b border-white w-full flex justify-end pb-1">{Math.round(simData.maxAxis * 0.66)}$</div>
                    <div className="border-b border-white w-full flex justify-end pb-1">{Math.round(simData.maxAxis * 0.33)}$</div>
                    <div className="w-full flex justify-end">0$</div>
                  </div>

                  {simData.stats.map((data, i) => (
                    <div key={i} className="flex-1 flex flex-col justify-end items-center group relative z-10 h-full">
                      
                      {/* Tooltip au survol */}
                      <div className="absolute top-0 opacity-0 group-hover:opacity-100 transition-opacity bg-cyan-400 text-slate-900 text-[10px] font-black px-2 py-0.5 rounded shadow-xl -translate-y-8 pointer-events-none">
                        {data.value}$
                      </div>
                      
                      {/* Barre du graphique avec hauteur dynamique correcte */}
                      <div 
                        className="w-full max-w-[30px] rounded-t-lg bg-gradient-to-t from-blue-600 to-cyan-300 transition-all duration-700 ease-out group-hover:from-blue-500 group-hover:to-cyan-200"
                        style={{ height: `${(data.value / simData.maxAxis) * 100}%`, minHeight: '5px' }}
                      ></div>
                      
                      <span className="text-[11px] mt-2 font-black text-slate-400 uppercase">{data.day}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ECOSYSTÈME AVEC LES LIENS EXTERNES RÉELS */}
        <div className="mb-24">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black">L'écosystème Forfeo</h2>
            <p className="text-slate-500 text-sm">5 outils performants pour transformer {realCompanyName}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: <FaShoppingBag/>, title: "Boutique", color: "blue", url: "https://forfeoexplore.com/boutique" },
              { icon: <FaBolt/>, title: "Offres Flash", color: "rose", url: "https://forfeoexplore.com/last-call" },
              { icon: <FaTicketAlt/>, title: "Prévente", color: "purple", url: "https://forfeoexplore.com/prevente" },
              { icon: <FaMapMarkerAlt/>, title: "Drops", color: "amber", url: "https://forfeoexplore.com/drops" },
              { icon: <FaRobot/>, title: "Assistant IA", color: "emerald", url: "https://forfeoexplore.com/service-client" }
            ].map((tool, i) => (
              <div 
                key={i} 
                onClick={() => window.open(tool.url, '_blank')} 
                className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className={`w-10 h-10 bg-${tool.color}-50 text-${tool.color}-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition`}>
                  {tool.icon}
                </div>
                <h4 className="font-black text-sm">{tool.title}</h4>
                <p className="text-[10px] text-slate-400 mt-1">Cliquez pour explorer ce module.</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA FINAL */}
        <div className="bg-slate-900 rounded-[3rem] p-5 md:p-10 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600/20 to-purple-600/20 mix-blend-overlay pointer-events-none"></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Prêt à activer {realCompanyName} ?</h2>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto text-sm">Votre environnement est pré-configuré. Activez votre compte pour commencer à recevoir des clients dès aujourd'hui.</p>
            <button 
              onClick={() => navigate(`/login?type=business&mode=signup&prefill=${encodeURIComponent(realCompanyName)}`)}
              className="bg-white text-slate-900 px-10 py-4 rounded-full font-black uppercase tracking-widest text-xs hover:scale-105 transition shadow-xl inline-flex items-center gap-3"
            >
              Lancer l'activation <FaArrowRight />
            </button>
          </div>
        </div>

      </div>
      
      <style dangerouslySetInnerHTML={{ __html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-in { animation: fade-in 0.8s ease-out forwards; }
      `}} />
    </div>
  );
}
